import type { ReactNode, MouseEvent } from 'react'
import { motion, useMotionValue, useSpring, useTransform, type MotionValue } from 'framer-motion'
import { LOGO_PATH, LOGO_ALT } from '@/lib/brand'
import { StoreImage } from '@/store/components/StoreImage'
import { ArabicWatermark } from '@/components/comingSoon/ComingSoonExtras'
import { COMING_SOON_CONTENT } from '@/lib/comingSoon'

function useParallax() {
  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const x = useSpring(useTransform(mx, [-1, 1], [-28, 28]), { stiffness: 40, damping: 20 })
  const y = useSpring(useTransform(my, [-1, 1], [-18, 18]), { stiffness: 40, damping: 20 })
  const xSlow = useSpring(useTransform(mx, [-1, 1], [-14, 14]), { stiffness: 30, damping: 22 })
  const ySlow = useSpring(useTransform(my, [-1, 1], [-10, 10]), { stiffness: 30, damping: 22 })

  const onMouseMove = (e: MouseEvent) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 2)
    my.set((e.clientY / window.innerHeight - 0.5) * 2)
  }

  return { x, y, xSlow, ySlow, onMouseMove }
}

interface AmbienceProps {
  x: MotionValue<number>
  y: MotionValue<number>
  xSlow: MotionValue<number>
  ySlow: MotionValue<number>
}

function MandalaRing() {
  return (
    <motion.div
      className="coming-soon-mandala absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2"
      animate={{ rotate: 360 }}
      transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
      aria-hidden
    >
      <svg width="520" height="520" viewBox="0 0 520 520" fill="none" className="opacity-[0.14] sm:opacity-[0.18]">
        <circle cx="260" cy="260" r="248" stroke="#D4AF37" strokeOpacity="0.25" strokeWidth="0.5" strokeDasharray="4 8" />
        <circle cx="260" cy="260" r="210" stroke="#C5A059" strokeOpacity="0.2" strokeWidth="0.5" />
        <circle cx="260" cy="260" r="170" stroke="#D4AF37" strokeOpacity="0.15" strokeWidth="0.5" strokeDasharray="2 6" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <line
            key={deg}
            x1="260"
            y1="260"
            x2={260 + 240 * Math.cos((deg * Math.PI) / 180)}
            y2={260 + 240 * Math.sin((deg * Math.PI) / 180)}
            stroke="#D4AF37"
            strokeOpacity="0.08"
            strokeWidth="0.5"
          />
        ))}
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
          <circle
            key={deg}
            cx={260 + 220 * Math.cos((deg * Math.PI) / 180)}
            cy={260 + 220 * Math.sin((deg * Math.PI) / 180)}
            r="3"
            fill="#F9E076"
            fillOpacity="0.35"
          />
        ))}
      </svg>
    </motion.div>
  )
}

const dust = Array.from({ length: 22 }, (_, i) => ({
  left: `${(i * 17.3) % 100}%`,
  top: `${(i * 23.7) % 100}%`,
  size: 1.5 + (i % 3),
  delay: i * 0.22,
  duration: 3 + (i % 6),
}))

export function ComingSoonAmbience({ x, y, xSlow, ySlow }: AmbienceProps) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div className="coming-soon-vignette absolute inset-0" />
      <div className="coming-soon-spotlight absolute inset-0" />
      <ArabicWatermark />

      <motion.div style={{ x: xSlow, y: ySlow }} className="absolute inset-0">
        <MandalaRing />
      </motion.div>

      <motion.div style={{ x, y }} className="absolute inset-0">
        <div className="coming-soon-orb coming-soon-orb-a" />
        <div className="coming-soon-orb coming-soon-orb-b" />
        <div className="coming-soon-orb coming-soon-orb-c" />
      </motion.div>

      {/* Rising scent columns — fewer, bolder */}
      {[18, 50, 82].map((left, i) => (
        <div
          key={left}
          className="coming-soon-scent-beam absolute bottom-0"
          style={{
            left: `${left}%`,
            animationDelay: `${i * 2.2}s`,
            animationDuration: `${8 + i}s`,
          }}
        />
      ))}

      {/* Gold dust */}
      {dust.map((p, i) => (
        <span
          key={i}
          className="coming-soon-dust absolute rounded-full"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}

      {/* Single accent bottle — desktop */}
      <motion.div
        className="coming-soon-side-bottle absolute hidden lg:block right-[8%] top-[34%]"
        animate={{ y: [0, -14, 0], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      >
        <svg viewBox="0 0 56 112" className="h-32 w-16 drop-shadow-[0_0_24px_rgba(212,175,55,0.2)]" aria-hidden>
          <defs>
            <linearGradient id="side-bottle" x1="28" y1="10" x2="28" y2="100">
              <stop stopColor="#F9E076" stopOpacity="0.45" />
              <stop offset="0.5" stopColor="#D4AF37" stopOpacity="0.6" />
              <stop offset="1" stopColor="#5c4522" stopOpacity="0.75" />
            </linearGradient>
          </defs>
          <ellipse cx="28" cy="72" rx="18" ry="26" fill="url(#side-bottle)" stroke="#D4AF37" strokeOpacity="0.45" strokeWidth="1" />
          <path d="M22 46 Q28 40 34 46 L31 24 Q28 18 25 24 Z" fill="url(#side-bottle)" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="0.75" />
          <rect x="23" y="10" width="10" height="14" rx="2" fill="#C5A059" fillOpacity="0.75" />
        </svg>
      </motion.div>
    </div>
  )
}

const valueIcons = [
  (
    <svg viewBox="0 0 32 40" className="h-6 w-6 sm:h-7 sm:w-7" fill="none" aria-hidden>
      <ellipse cx="16" cy="26" rx="10" ry="12" fill="#D4AF37" fillOpacity="0.15" stroke="#D4AF37" strokeOpacity="0.6" strokeWidth="1" />
      <path d="M13 14 L16 8 L19 14 Z" fill="#C5A059" fillOpacity="0.5" />
      <rect x="14" y="6" width="4" height="6" rx="1" fill="#F9E076" fillOpacity="0.4" />
    </svg>
  ),
  (
    <svg viewBox="0 0 32 32" className="h-6 w-6 sm:h-7 sm:w-7" fill="none" aria-hidden>
      <circle cx="16" cy="16" r="11" stroke="#D4AF37" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="3 3" />
      <path d="M16 8 V16 L21 19" stroke="#F9E076" strokeOpacity="0.7" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  ),
  (
    <svg viewBox="0 0 32 32" className="h-6 w-6 sm:h-7 sm:w-7" fill="none" aria-hidden>
      <path d="M8 22 C8 14 16 8 16 8 C16 8 24 14 24 22" stroke="#D4AF37" strokeOpacity="0.6" strokeWidth="1" fill="none" />
      <path d="M10 22 H22" stroke="#C5A059" strokeOpacity="0.5" strokeWidth="0.75" />
      <circle cx="16" cy="22" r="2" fill="#F9E076" fillOpacity="0.5" />
    </svg>
  ),
]

export function BrandValues() {
  return (
    <div className="mt-8 grid w-full grid-cols-3 gap-px overflow-hidden rounded-xl border border-gold-500/10 bg-gold-500/10">
      {COMING_SOON_CONTENT.values.map((item, i) => (
        <motion.div
          key={item.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 + i * 0.08, duration: 0.45 }}
          className="coming-soon-value flex flex-col items-center bg-luxury-950/80 px-2 py-4 sm:px-4 sm:py-5"
        >
          <div className="mb-2 text-gold-400/80">{valueIcons[i]}</div>
          <p className="font-display text-xs font-semibold text-gold-200 sm:text-sm">{item.label}</p>
          <p className="mt-0.5 text-center text-[9px] leading-tight text-zinc-500 sm:text-[10px]">{item.desc}</p>
        </motion.div>
      ))}
    </div>
  )
}

export function HeroLogo() {
  return (
    <div className="relative mx-auto mb-5 flex justify-center">
      <motion.div
        className="coming-soon-logo-halo absolute h-36 w-36 sm:h-44 sm:w-44"
        animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
        className="coming-soon-logo-ring relative rounded-full p-1"
      >
        <StoreImage
          src={LOGO_PATH}
          alt={LOGO_ALT}
          objectFit="contain"
          spinnerSize="lg"
          containerClassName="flex h-24 w-24 items-center justify-center sm:h-32 sm:w-32"
          className="h-24 w-24 drop-shadow-[0_0_40px_rgba(212,175,55,0.35)] sm:h-32 sm:w-32"
        />
      </motion.div>
    </div>
  )
}

export function OrnateDivider() {
  return (
    <div className="my-5 flex items-center justify-center gap-4" aria-hidden>
      <span className="h-px w-16 bg-gradient-to-r from-transparent to-gold-500/40 sm:w-24" />
      <span className="h-1.5 w-1.5 rotate-45 border border-gold-400/50 bg-gold-500/20" />
      <span className="h-px w-16 bg-gradient-to-l from-transparent to-gold-500/40 sm:w-24" />
    </div>
  )
}

export function HeroCard({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] as const }}
      className="coming-soon-card-wrap relative w-full max-w-xl overflow-hidden rounded-2xl sm:max-w-2xl"
    >
      <div className="coming-soon-card-border" aria-hidden />
      <div className="coming-soon-card-inner relative m-px overflow-hidden rounded-[15px] px-5 py-8 sm:px-10 sm:py-11">
        <div className="coming-soon-card-texture pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative z-[1]">
        {children}
        </div>
      </div>
    </motion.div>
  )
}

export { useParallax }
